import { resolveWalletConnectWallets } from './registry';
const cacheByNamespace = new Map();
const inFlightByKey = new Map();
const DEFAULT_PAGE_SIZE = 40;
const MAX_CACHED_PAGES_PER_QUERY = 20;
const MAX_CACHED_QUERIES_PER_NAMESPACE = 50;
const DEFAULT_PROJECT_ID = '6113382c2e587bff00e2b5c3d68531f3';
const normalizeQuery = (query) => query?.trim() ?? '';
const getNamespaceCache = (namespace) => {
    if (!cacheByNamespace.has(namespace))
        cacheByNamespace.set(namespace, new Map());
    return cacheByNamespace.get(namespace);
};
const getQueryCache = (namespace, query) => {
    const namespaceCache = getNamespaceCache(namespace);
    if (!namespaceCache.has(query)) {
        if (namespaceCache.size >= MAX_CACHED_QUERIES_PER_NAMESPACE) {
            for (const existingKey of namespaceCache.keys()) {
                if (existingKey !== '') {
                    namespaceCache.delete(existingKey);
                    break;
                }
            }
        }
        namespaceCache.set(query, new Map());
    }
    return namespaceCache.get(query);
};
const getPageKey = (page, pageSize) => `${page}:${pageSize}`;
const listCachedPages = (namespace, query) => Array.from(getQueryCache(namespace, query).values()).sort((l, r) => l.page - r.page);
const mergeRecents = (recents, connectors) => {
    if (recents.length === 0)
        return connectors;
    const recentIds = new Set(recents.map(c => c.id.toLowerCase()));
    return [...recents, ...connectors.filter(c => !recentIds.has(c.id.toLowerCase()))];
};
async function fetchAdditionalConnectorsPage(namespace, params) {
    const query = normalizeQuery(params.query);
    const pageKey = getPageKey(params.page, params.pageSize);
    const queryCache = getQueryCache(namespace, query);
    const cached = queryCache.get(pageKey);
    if (cached)
        return cached.result;
    const inFlightKey = `${namespace}:${query}:${pageKey}`;
    const existingRequest = inFlightByKey.get(inFlightKey);
    if (existingRequest)
        return existingRequest;
    const request = (async () => {
        const result = await resolveWalletConnectWallets({
            namespace,
            page: params.page,
            entries: params.pageSize,
            search: query || undefined,
            projectId: params.projectId,
        });
        if (queryCache.size >= MAX_CACHED_PAGES_PER_QUERY) {
            const oldestKey = queryCache.keys().next().value;
            if (oldestKey)
                queryCache.delete(oldestKey);
        }
        queryCache.set(pageKey, { page: params.page, pageSize: params.pageSize, result });
        return result;
    })();
    inFlightByKey.set(inFlightKey, request);
    try {
        return await request;
    }
    finally {
        inFlightByKey.delete(inFlightKey);
    }
}
const EMPTY_SNAPSHOT = Object.freeze({
    browseConnectors: Object.freeze([]),
    browseMetadata: Object.freeze({ loaded: false, nextPage: null, totalCount: 0, status: 'idle' }),
});
function createStore(namespace, projectId) {
    let state = { recents: [], browseVersion: 0, browseStatus: 'idle' };
    let snapshot = EMPTY_SNAPSHOT;
    let ensureInFlight = null;
    const listeners = new Set();
    // Snapshot inputs behind the last emit. Downstream connection snapshots
    // (EVM/SVM) compare `browseConnectors` by identity, so rebuilding the
    // array when neither the cached pages nor the recents changed would
    // falsely invalidate their memoization.
    let lastPages = [];
    let lastRecents = state.recents;
    let lastStatus = 'idle';
    const computeSnapshot = () => {
        const cachedBrowsePages = listCachedPages(namespace, '');
        // Cached pages are the source of truth: another store instance sharing
        // this namespace cache may have loaded them, so `ready` wins over
        // whatever this instance's own fetch lifecycle says.
        const status = cachedBrowsePages.length > 0 ? 'ready' : state.browseStatus;
        const pagesUnchanged = cachedBrowsePages.length === lastPages.length
            && cachedBrowsePages.every((page, i) => page === lastPages[i]);
        if (pagesUnchanged && state.recents === lastRecents && status === lastStatus)
            return snapshot;
        lastPages = cachedBrowsePages;
        lastRecents = state.recents;
        lastStatus = status;
        const connectors = cachedBrowsePages.flatMap(page => page.result.wallets);
        const browseConnectors = mergeRecents(state.recents, connectors);
        const lastPage = cachedBrowsePages[cachedBrowsePages.length - 1];
        return {
            browseConnectors,
            browseMetadata: {
                loaded: cachedBrowsePages.length > 0,
                nextPage: lastPage?.result.nextPage ?? null,
                totalCount: lastPage?.result.totalCount ?? 0,
                status,
            },
        };
    };
    const emit = () => {
        const next = computeSnapshot();
        if (next === snapshot)
            return;
        snapshot = next;
        listeners.forEach(l => l());
        notifyStoresListeners();
    };
    snapshot = computeSnapshot();
    const requestAdditionalConnectors = async (params = {}) => {
        const normalized = {
            page: params.page ?? 1,
            pageSize: params.pageSize ?? DEFAULT_PAGE_SIZE,
            query: normalizeQuery(params.query),
            projectId,
        };
        const isBrowse = !normalized.query;
        if (isBrowse && !snapshot.browseMetadata.loaded && state.browseStatus !== 'loading') {
            state = { ...state, browseStatus: 'loading' };
            emit();
        }
        try {
            const result = await fetchAdditionalConnectorsPage(namespace, normalized);
            if (isBrowse) {
                state = { ...state, browseVersion: state.browseVersion + 1, browseStatus: 'ready' };
                emit();
            }
            return {
                connectors: result.wallets,
                nextPage: result.nextPage,
                totalCount: result.totalCount,
            };
        }
        catch (error) {
            if (isBrowse && !snapshot.browseMetadata.loaded) {
                state = { ...state, browseStatus: 'error' };
                emit();
            }
            throw error;
        }
    };
    return {
        subscribe(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        getSnapshot() {
            return snapshot;
        },
        requestAdditionalConnectors,
        async ensureBrowseLoaded() {
            if (snapshot.browseMetadata.loaded)
                return;
            if (ensureInFlight)
                return ensureInFlight;
            ensureInFlight = (async () => {
                try {
                    await requestAdditionalConnectors({ page: 1, pageSize: DEFAULT_PAGE_SIZE });
                }
                catch (error) {
                    console.warn(`Failed to load WalletConnect wallets registry (${namespace})`, error);
                }
                finally {
                    ensureInFlight = null;
                }
            })();
            return ensureInFlight;
        },
        addRecentConnector(connector) {
            const deduped = state.recents.filter(item => item.id.toLowerCase() !== connector.id.toLowerCase());
            state = { ...state, recents: [connector, ...deduped] };
            emit();
        },
    };
}
const storeRegistry = new Map();
// Cross-store listeners: fired when any namespace store emits or a new store
// is instantiated (a lazily-hydrated wallet package creating its store must
// wake widget-side readiness checks that enumerate instantiated stores).
const storesListeners = new Set();
const notifyStoresListeners = () => {
    storesListeners.forEach(l => l());
};
export function subscribeAdditionalConnectorsStores(listener) {
    storesListeners.add(listener);
    return () => { storesListeners.delete(listener); };
}
export function getInstantiatedAdditionalConnectorsStores() {
    return Array.from(storeRegistry.values());
}
/**
 * Fire-and-forget warm-up of the page-1 browse fetch for every namespace
 * store that exists so far. Also serves as the retry path after an `error`
 * status — `ensureBrowseLoaded` re-attempts failed fetches.
 */
export function ensureRegistryBrowseLoaded() {
    for (const store of storeRegistry.values())
        void store.ensureBrowseLoaded();
}
export function getAdditionalConnectorsStore(namespace, projectId) {
    const resolvedProjectId = projectId ?? DEFAULT_PROJECT_ID;
    const key = `${namespace}:${resolvedProjectId}`;
    let store = storeRegistry.get(key);
    if (!store) {
        store = createStore(namespace, resolvedProjectId);
        storeRegistry.set(key, store);
        notifyStoresListeners();
    }
    return store;
}
